import type { ProductGridRowDto, ProductGridSaveRequest, ProductStatus } from '@ecom/contracts';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { parseCount } from '@/lib/stock';

/**
 * The product grid's cells (ADR-090): what a row shows before anyone types,
 * whether a typed cell can be sent, and the request for every changed one.
 */

export type ProductCell = 'nameEn' | 'nameAr' | 'status' | 'basePrice';
export type VariantCell = 'sku' | 'price' | 'costPrice' | 'stock' | 'barcode' | 'mpn' | 'weightGrams';

/** A row's cell as the grid shows it before anyone types. */
export function productCell(row: ProductGridRowDto, cell: ProductCell): string {
  if (cell === 'nameEn') return row.name.en;
  if (cell === 'nameAr') return row.name.ar;
  if (cell === 'status') return row.status;
  return halalasToRiyals(row.basePrice);
}

export function variantCell(row: ProductGridRowDto, cell: VariantCell): string {
  switch (cell) {
    case 'price':
      return row.price === null ? '' : halalasToRiyals(row.price);
    case 'costPrice':
      return row.costPrice === null ? '' : halalasToRiyals(row.costPrice);
    case 'stock':
      return String(row.stock);
    case 'weightGrams':
      return row.weightGrams === null ? '' : String(row.weightGrams);
    default:
      return row[cell] ?? '';
  }
}

/** Whether a typed cell can be sent: a price in riyals, a whole count, a SKU not empty. */
export function cellValid(cell: ProductCell | VariantCell, value: string): boolean {
  const text = value.trim();
  if (cell === 'basePrice') return riyalsToHalalas(text) !== null;
  if (cell === 'price' || cell === 'costPrice') return text === '' || riyalsToHalalas(text) !== null;
  if (cell === 'stock') return parseCount(text) !== null;
  if (cell === 'weightGrams') return text === '' || parseCount(text) !== null;
  if (cell === 'sku' || cell === 'nameEn') return text !== '';
  return true;
}

/** The request for every cell that differs from what was loaded; null while any is invalid. */
export function changesOf(state: {
  productEdits: Record<string, Partial<Record<ProductCell, string>>>;
  variantEdits: Record<string, Partial<Record<VariantCell, string>>>;
  originals: Record<string, ProductGridRowDto>;
}): ProductGridSaveRequest | null {
  const products: NonNullable<ProductGridSaveRequest['products']> = [];
  const variants: NonNullable<ProductGridSaveRequest['variants']> = [];
  for (const [id, edits] of Object.entries(state.productEdits)) {
    const row = state.originals[`p:${id}`];
    if (!row) continue;
    const change: (typeof products)[number] = { id };
    for (const [cell, value] of Object.entries(edits) as [ProductCell, string][]) {
      if (value === productCell(row, cell)) continue;
      if (!cellValid(cell, value)) return null;
      if (cell === 'nameEn') change.name = { ...change.name, en: value.trim() };
      if (cell === 'nameAr') change.name = { ...change.name, ar: value.trim() };
      if (cell === 'status') change.status = value as ProductStatus;
      if (cell === 'basePrice') change.basePrice = riyalsToHalalas(value.trim()) as number;
    }
    if (Object.keys(change).length > 1) products.push(change);
  }
  for (const [id, edits] of Object.entries(state.variantEdits)) {
    const row = state.originals[`v:${id}`];
    if (!row) continue;
    const change: (typeof variants)[number] = { id };
    for (const [cell, value] of Object.entries(edits) as [VariantCell, string][]) {
      if (value === variantCell(row, cell)) continue;
      if (!cellValid(cell, value)) return null;
      const text = value.trim();
      if (cell === 'sku') change.sku = text;
      if (cell === 'price') change.price = text === '' ? null : riyalsToHalalas(text);
      // What one unit cost, without VAT (ADR-118); empty clears it.
      if (cell === 'costPrice') change.costPrice = text === '' ? null : riyalsToHalalas(text);
      if (cell === 'stock') change.stock = parseCount(text) as number;
      if (cell === 'barcode') change.barcode = text || null;
      if (cell === 'mpn') change.mpn = text || null;
      if (cell === 'weightGrams') change.weightGrams = text === '' ? null : parseCount(text);
    }
    if (Object.keys(change).length > 1) variants.push(change);
  }
  return { products, variants };
}
